import { formatCPF } from "@brazilian-utils/brazilian-utils";
import { Box, Flex, Link, Text } from "@chakra-ui/layout";
import {
  Alert,
  AlertIcon,
  Divider,
  Heading,
  Image,
  Radio,
  RadioGroup,
} from "@chakra-ui/react";
import { Col, Modal, Row } from "antd";
import ButtonAnexarArquivos from "components/Button/ButtonAnexarArquivos";
import { useLoading } from "components/GlobalLoading";
import ModalCamera from "components/ModalCamera/index";
import useDadosPessoaPortaria from "core/features/portaria/hooks/useDadosPessoaPortaria";
import { ConfirmarDadosPortariaForm } from "core/features/portaria/typings";
import { TipoEntidadeEnum } from "core/resources/enums/conta";
import colors from "core/resources/theme/colors";
import base64ToFile from "infra/helpers/base64ToFile";
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { AiOutlineCamera } from "react-icons/ai";
import Button from "ui/components/Button";
import FooterModal from "ui/components/Button/FooterModal";
import Field from "ui/components/Input/Field";
import InputCpfCnpj from "ui/components/Input/InputCpfCnpj";
import SelectPessoas from "ui/components/Input/SelectPessoas";

interface ModalConfirmarDadosPessoaProps {
  visible: boolean;
  onSubmit: (form: ConfirmarDadosPortariaForm) => any;
  onClose: () => any;
  isLoadingConfirmar?: boolean;
  portariaPessoaId?: string;
  portariaAutorizacaoId?: string;
}

function ModalConfirmarDadosPessoa({
  visible,
  onSubmit,
  onClose,
  isLoadingConfirmar,
  portariaPessoaId,
  portariaAutorizacaoId,
}: ModalConfirmarDadosPessoaProps) {
  const { control, handleSubmit, watch, setValue } =
    useForm<ConfirmarDadosPortariaForm>({
      mode: "all",
      defaultValues: portariaPessoaId
        ? {
            pessoaExistente: "1",
            pessoaId: portariaPessoaId,
          }
        : undefined,
    });

  const form = watch();

  const { isLoading, data } = useDadosPessoaPortaria(
    form.pessoaId,
    portariaAutorizacaoId,
  );
  const [modalCameraTarget, setModalCameraTarget] = useState<
    "arquivoDocumento" | "arquivoFoto" | null
  >(null);

  useLoading(isLoading);

  useEffect(() => {
    if (data) {
      setValue("documento", data.documento ?? undefined);
      setValue("cnh", data.cnh ?? undefined);
      setValue("placa", data.placa ?? undefined);
    }
  }, [data, setValue]);

  const urlFoto = form?.arquivoFoto
    ? URL.createObjectURL(form.arquivoFoto)
    : data?.foto;
  const urlImagemDocumento = form?.arquivoDocumento
    ? URL.createObjectURL(form.arquivoDocumento)
    : data?.imagemDocumento;

  const getIsDisabled = () => {
    if (form.pessoaExistente === "1") {
      return !form.pessoaId;
    }

    return !form.nome || !form.documento;
  };
  return (
    <Modal
      width={"750px"}
      footer={
        <FooterModal
          showRegisterAnother={false}
          registerAnother={false}
          isLoadingSave={isLoadingConfirmar}
          onSubmit={handleSubmit(onSubmit)}
          onClose={onClose}
          primaryButtonText={"Confirmar"}
          buttonCancelarText="Voltar"
          primaryButtonBgColor={colors.green500}
          isDisabled={getIsDisabled()}
        />
      }
      visible={visible}
      onCancel={onClose}
    >
      <Flex flexDir="column">
        <Heading mb={4} size="md">
          Confirmar dados de quem irá entrar
        </Heading>
        <Alert status="info" mb={4} fontSize={12}>
          <AlertIcon />
          Ao clicar em confirmar, os dados serão salvos e a autorização será
          confirmada automaticamente.
        </Alert>
      </Flex>

      <Divider mb={4} />

      <Field
        control={control}
        name="pessoaExistente"
        rules={{
          required: true,
        }}
        render={({ field, isInvalid }) => (
          <RadioGroup
            value={field.value}
            onChange={field.onChange}
            as={Row}
            gutter={32}
            mb={6}
          >
            <Box as={Col} md={6}>
              <Radio
                isInvalid={isInvalid}
                color={isInvalid ? "red" : undefined}
                value={"1"}
              >
                Pessoa Existente
              </Radio>
            </Box>
            <Box as={Col} md={6}>
              <Radio
                isInvalid={isInvalid}
                color={isInvalid ? "red" : undefined}
                value={"0"}
              >
                Nova Pessoa
              </Radio>
            </Box>
          </RadioGroup>
        )}
      />

      {form.pessoaExistente === "1" ? (
        <>
          <Field
            label="Pessoa"
            name="pessoaId"
            control={control}
            shouldUnregister
            rules={{ required: "Campo obrigatório" }}
            render={({ field, isInvalid }) => (
              <SelectPessoas
                semDocumentoDuplicado={true}
                somenteComDocumento={true}
                showSearch
                tenantId={null}
                isInvalid={isInvalid}
                tipoEntidades={[TipoEntidadeEnum.Outros]}
                {...field}
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />

          {data?.documento ? (
            <Text mt={2}>Documento: {formatCPF(data.documento)}</Text>
          ) : null}
        </>
      ) : null}

      {form.pessoaExistente === "0" ? (
        <>
          <Box mb={4}>
            <Field
              label="Nome"
              name="nome"
              control={control}
              shouldUnregister
              rules={{ required: "Campo obrigatório" }}
            />
          </Box>

          <Box mb={4}>
            <Field
              label="CPF"
              name="documento"
              control={control}
              shouldUnregister
              rules={{ required: "Campo obrigatório" }}
              render={({ field, isInvalid }) => (
                <InputCpfCnpj
                  maxLength={14}
                  isInvalid={isInvalid}
                  placeholder="CPF de quem irá entrar"
                  {...field}
                />
              )}
            />
          </Box>
        </>
      ) : null}

      {!!form.pessoaExistente ? (
        <>
          {!data?.documento ? (
            <Box as={Row} gutter={32} my={4}>
              <Box as={Col} md={24}>
                <Field
                  label="CPF"
                  name="documento"
                  control={control}
                  shouldUnregister
                  rules={{ required: "Campo obrigatório" }}
                  render={({ field, isInvalid }) => (
                    <InputCpfCnpj
                      maxLength={14}
                      isInvalid={isInvalid}
                      placeholder="CPF"
                      {...field}
                    />
                  )}
                />
              </Box>
            </Box>
          ) : null}

          <Box as={Row} gutter={32} my={4}>
            <Box as={Col} md={12}>
              <Field
                label="Placa"
                name="placa"
                control={control}
                inputProps={{ placeholder: "Placa do veículo" }}
                shouldUnregister
              />
            </Box>
            <Box as={Col} md={12}>
              <Field
                label="CNH"
                name="cnh"
                control={control}
                inputProps={{ placeholder: "CNH" }}
                shouldUnregister
              />
            </Box>
          </Box>

          <Box as={Row} gutter={32}>
            <Box as={Col} md={12}>
              <Flex flex={1} alignItems="center" gap={1}>
                <Text fontWeight="500" fontSize={18}>
                  Foto
                </Text>

                <ButtonAnexarArquivos
                  accept="image/*"
                  buttonText=""
                  maxFileSizeMb={25}
                  currentFilesLength={0}
                  variant="ghost"
                  onAnexar={(files) => {
                    if (files) {
                      setValue("arquivoFoto", files[0]);
                    }
                  }}
                />

                <Button
                  variant="ghost"
                  onClick={() => setModalCameraTarget("arquivoFoto")}
                >
                  <AiOutlineCamera color={colors.primary} size={20} />
                </Button>
              </Flex>

              <Flex
                gap="16px"
                mt="auto"
                h="fit-content"
                py={2}
                flexDir="column"
                alignItems="center"
              >
                {urlFoto ? (
                  <Link
                    href={urlFoto}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Image src={urlFoto} borderRadius={5} h="240px" />
                  </Link>
                ) : null}
              </Flex>
            </Box>

            <Box as={Col} md={12}>
              <Flex flex={1} gap={1} alignItems="center">
                <Text fontWeight="500" fontSize={18}>
                  Foto do documento
                </Text>

                <ButtonAnexarArquivos
                  buttonText=""
                  accept="image/*"
                  maxFileSizeMb={25}
                  currentFilesLength={0}
                  variant="ghost"
                  onAnexar={(files) => {
                    if (files) {
                      setValue("arquivoDocumento", files[0]);
                    }
                  }}
                />

                <Button
                  variant="ghost"
                  onClick={() => setModalCameraTarget("arquivoDocumento")}
                >
                  <AiOutlineCamera color={colors.primary} size={20} />
                </Button>
              </Flex>

              <Flex
                gap="16px"
                mt="auto"
                h="fit-content"
                py={2}
                flexDir="column"
                alignItems="center"
              >
                {urlImagemDocumento ? (
                  <Link
                    href={urlImagemDocumento}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Image
                      src={urlImagemDocumento}
                      borderRadius={5}
                      h="240px"
                    />
                  </Link>
                ) : null}
              </Flex>
            </Box>
          </Box>
        </>
      ) : null}

      {modalCameraTarget ? (
        <ModalCamera
          croppable
          visible={!!modalCameraTarget}
          onClose={() => setModalCameraTarget(null)}
          onSubmit={(base64) => {
            const file = base64ToFile(base64);

            setValue(modalCameraTarget, file);
          }}
        />
      ) : null}
    </Modal>
  );
}

export default ModalConfirmarDadosPessoa;
